// Checkout attempt bookkeeping: one random Idempotency-Key per attempt, tied to
// a fingerprint of the cart. No imports, so it can be unit-tested on its own.
//
// A retry of the SAME cart (double-click, lost response, checkout_in_progress)
// reuses the key so PlazaStack returns the same session instead of reserving
// stock twice. The attempt must outlive the reservation (30 minutes), so it is
// kept 35 minutes: a lost response retried at minute 26 still finds its key.
// Once a session exists the attempt records its id, and only confirming THAT
// session retires the attempt.

export type StoreName = 'localStorage' | 'sessionStorage';

const ATTEMPT_KEY = 'np:checkout-attempt';
export const ATTEMPT_TTL_MS = 35 * 60 * 1000;

interface Attempt { fp: string; key: string; at: number; sessionId?: string }

// Even touching window.localStorage can throw (blocked site data), so every
// access goes through these and failures degrade instead of throwing.
export function readJson<T>(store: StoreName, key: string): T | null {
  try {
    const raw = window[store].getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function writeJson(store: StoreName, key: string, value: unknown): boolean {
  try {
    window[store].setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function removeKey(store: StoreName, key: string): void {
  try {
    window[store].removeItem(key);
  } catch {
    // nothing to do
  }
}

function randomKey(): string {
  try {
    return `np-${crypto.randomUUID()}`;
  } catch {
    return `np-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  }
}

// In-memory copy so a retry on the same page reuses the key even when
// sessionStorage is unavailable.
let memoryAttempt: Attempt | null = null;

/** Test hook: forget the in-memory copy. */
export function resetAttemptMemory(): void {
  memoryAttempt = null;
}

// Every use renews the clock: the TTL counts from the LATEST request, so the
// key always outlives the newest reservation it could have created (a retry
// at minute 26 is still covered at minute 56).
function renew(a: Attempt): string {
  const renewed = { ...a, at: Date.now() };
  memoryAttempt = renewed;
  writeJson('sessionStorage', ATTEMPT_KEY, renewed);
  return renewed.key;
}

export function attemptKeyFor(fingerprint: string): string {
  // No time limit: a key is retired only when the cart changes, PlazaStack
  // reports its session expired (stock released), or the order is paid.
  const fresh = (a: Attempt | null) => !!a && a.fp === fingerprint;
  // This page's own attempt wins: if storage writes are failing, a stale
  // persisted entry must not hide it and mint a second key for the same cart.
  if (memoryAttempt && fresh(memoryAttempt)) return renew(memoryAttempt);
  const stored = readJson<Attempt>('sessionStorage', ATTEMPT_KEY);
  if (stored && fresh(stored)) return renew(stored);
  const attempt: Attempt = { fp: fingerprint, key: randomKey(), at: Date.now() };
  memoryAttempt = attempt;
  writeJson('sessionStorage', ATTEMPT_KEY, attempt);
  return attempt.key;
}

/** Drop the current attempt outright (its replayed session has expired). */
export function dropCurrentAttempt(): void {
  memoryAttempt = null;
  removeKey('sessionStorage', ATTEMPT_KEY);
}

/** Record the session this attempt produced. */
export function noteAttemptSession(key: string, sessionId: string): void {
  if (memoryAttempt && memoryAttempt.key === key) memoryAttempt = { ...memoryAttempt, sessionId };
  const stored = readJson<Attempt>('sessionStorage', ATTEMPT_KEY);
  if (stored && stored.key === key) writeJson('sessionStorage', ATTEMPT_KEY, { ...stored, sessionId });
}

/**
 * Retire the attempt, but only if it belongs to this session. Confirming an
 * older session must not rotate the key of the cart being checked out now.
 */
export function retireAttemptForSession(sessionId: string): void {
  const stored = readJson<Attempt>('sessionStorage', ATTEMPT_KEY);
  if (stored && stored.sessionId === sessionId) removeKey('sessionStorage', ATTEMPT_KEY);
  if (memoryAttempt && memoryAttempt.sessionId === sessionId) memoryAttempt = null;
}
